import { z } from 'zod';
import * as dt from './data.types.js';

const gatewaySerialNumberParamSchema = z.string().min(1).max(255).trim();
const containerSlotSchema = z.coerce.number().int().min(1).max(25);
const loadPortIdSchema = z.coerce.number().int().min(1).max(4);
const integerIdSchema = z.coerce.number().int().positive();
const BaseResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

export const batchWaferTransferJobPhaseSchema = z.nativeEnum(dt.BATCH_WAFER_TRANSFER_JOB_PHASE);
export const batchWaferTransferItemPhaseSchema = z.nativeEnum(dt.BATCH_WAFER_TRANSFER_ITEM_PHASE);
export const batchWaferTransferItemOutcomeSchema = z.nativeEnum(
  dt.BATCH_WAFER_TRANSFER_ITEM_OUTCOME,
);

export const batchWaferTransferItemRequestSchema = z.object({
  fromLoadPort: loadPortIdSchema,
  fromSlot: containerSlotSchema,
  fromContainerId: integerIdSchema,
  toLoadPort: loadPortIdSchema,
  toSlot: containerSlotSchema,
  toContainerId: integerIdSchema,
});

export const initiateBatchWaferTransferRequestSchema = z
  .object({
    gatewaySerialNumber: gatewaySerialNumberParamSchema,
    transfers: z.array(batchWaferTransferItemRequestSchema).min(1).max(25),
    comment: z.string().max(500).trim().optional(),
  })
  .refine(
    (data) => {
      const sourceSlots = new Set<string>();
      for (const t of data.transfers) {
        const key = `${t.fromLoadPort}-${t.fromSlot}`;
        if (sourceSlots.has(key)) return false;
        sourceSlots.add(key);
      }
      return true;
    },
    { message: 'Duplicate source slots are not allowed' },
  )
  .refine(
    (data) => {
      const targetSlots = new Set<string>();
      for (const t of data.transfers) {
        const key = `${t.toLoadPort}-${t.toSlot}`;
        if (targetSlots.has(key)) return false;
        targetSlots.add(key);
      }
      return true;
    },
    { message: 'Duplicate target slots are not allowed' },
  );


export { waferTransferJobIdParamSchema, tenantJobIdParamSchema, publicTenantJobIdSchema } from './tenant-job-id.schemas.js';

export const initiateBatchWaferTransferResponseSchema = BaseResponseSchema.extend({
  data: z.object({ jobId: z.string() }),
});

export const waferTransferProgressMessageSchema = z.object({
  jobId: z.string(),
  transferId: z.string(),
  transferIndex: z.number().int().min(0),
  status: z.string(),
  eventSeq: z.number().int().positive().optional(),
  fromLoadPort: loadPortIdSchema,
  fromSlot: containerSlotSchema,
  toLoadPort: loadPortIdSchema,
  toSlot: containerSlotSchema,
  result: z
    .object({
      durationMs: z.number().int().min(0).optional(),
      error: z.string().optional(),
    })
    .optional(),
  timestamp: z.string(),
});


export const waferTransferTelemetryEventSchema = z.object({
  type: z.literal('transfer_progress'),
  tenantId: z.string().uuid(),
  jobId: z.string(),
  transferId: z.string(),
  transferIndex: z.number().int().min(0),
  status: z.string(),
  eventSeq: z.number().int().positive().optional(),
  fromLoadPort: loadPortIdSchema,
  fromSlot: containerSlotSchema,
  toLoadPort: loadPortIdSchema,
  toSlot: containerSlotSchema,
  result: waferTransferProgressMessageSchema.shape.result.optional(),
  timestamp: z.string(),
});


export const batchWaferTransferProgressItemSchema = z.object({
  transferId: z.string(),
  transferIndex: z.number().int().min(0),
  fromLoadPort: loadPortIdSchema,
  fromSlot: containerSlotSchema,
  toLoadPort: loadPortIdSchema,
  toSlot: containerSlotSchema,
  fromContainerId: integerIdSchema,
  toContainerId: integerIdSchema,
  transferPhase: batchWaferTransferItemPhaseSchema,
  outcome: batchWaferTransferItemOutcomeSchema.optional(),
  errorMessage: z.string().optional(),
  durationMs: z.number().int().min(0).optional(),
});

export const batchWaferTransferJobProgressSchema = z.object({
  jobId: z.string(),
  phase: batchWaferTransferJobPhaseSchema,
  totalTransfers: z.number().int().min(1),
  completedTransfers: z.number().int().min(0),
  successfulTransfers: z.number().int().min(0),
  failedTransfers: z.number().int().min(0),
  items: z.array(batchWaferTransferProgressItemSchema),
  timing: z.object({
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
  }),
  phaseError: z.string().optional(),
});


export const batchWaferTransferJobProgressResponseSchema = BaseResponseSchema.extend({
  data: batchWaferTransferJobProgressSchema,
});

export const executeBatchWaferTransferCommandSchema = z.object({
  command: z.literal('execute_batch_wafer_transfer'),
  jobId: z.string().min(1),
  transfers: z.array(
    z.object({
      transferId: z.string(),
      fromLoadPort: loadPortIdSchema,
      fromSlot: containerSlotSchema,
      toLoadPort: loadPortIdSchema,
      toSlot: containerSlotSchema,
    }),
  ),
  options: z.object({ timeoutMs: z.number().int().positive().optional() }).optional(),
});

export const batchWaferTransferActiveJobSchema = z.object({
  jobId: z.string(),
  phase: batchWaferTransferJobPhaseSchema,
  efemClientId: integerIdSchema,
  totalTransfers: z.number().int().min(0),
  completedTransfers: z.number().int().min(0),
  phaseError: z.string().nullable().optional(),
  initiatedByUserId: integerIdSchema,
  initiatedByEmail: z.string().nullable().optional(),
  startedAt: z.string().optional(),
  finishedAt: z.string().nullable().optional(),
  updatedAt: z.string(),
});


export const batchWaferTransferStuckReasonSchema = z.enum(['transferring_idle', 'never_started']);

export const batchWaferTransferStuckJobSchema = z.object({
  jobId: z.string(),
  tenantId: z.string().uuid(),
  phase: batchWaferTransferJobPhaseSchema,
  efemClientId: integerIdSchema,
  stuckReason: batchWaferTransferStuckReasonSchema,
  lastItemActivityAt: z.string().nullable(),
  transferringStartedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
// Inferred types
export type BatchWaferTransferActiveJob = z.infer<typeof batchWaferTransferActiveJobSchema>;
export type BatchWaferTransferJobProgress = z.infer<typeof batchWaferTransferJobProgressSchema>;
export type BatchWaferTransferProgressItem = z.infer<typeof batchWaferTransferProgressItemSchema>;
export type BatchWaferTransferStuckJob = z.infer<typeof batchWaferTransferStuckJobSchema>;
export type InitiateBatchWaferTransferRequest = z.infer<
  typeof initiateBatchWaferTransferRequestSchema
>;
export type WaferTransferProgressMessage = z.infer<typeof waferTransferProgressMessageSchema>;
export type WaferTransferTelemetryEvent = z.infer<typeof waferTransferTelemetryEventSchema>;
